import { ref } from 'vue';

/**
 * Show smart-hardware menu when embedded from Baiyu.
 * In cross-origin iframe, login redirects drop document.referrer — persist context
 * on first load and support explicit query / postMessage from the parent shell.
 */
const HARDWARE_MENU_URL_MARKERS = ['baiyu'];
const STORAGE_KEY = 'sbt_baiyu_hardware_embed';
const QUERY_MARKER_KEYS = ['embed', 'from', 'source', 'parentHost'];

/** Reactive flag for layout menus (updated on resolve / postMessage). */
export const baiyuHardwareMenuEnabled = ref(false);

function markEnabled() {
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Private mode / blocked storage
  }
  baiyuHardwareMenuEnabled.value = true;
}

function isMarkedEnabled() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function matchesMarker(url) {
  return HARDWARE_MENU_URL_MARKERS.some(marker =>
    String(url).toLowerCase().includes(marker)
  );
}

function collectBrowserUrls() {
  const urls = [window.location.href];
  if (document.referrer) {
    urls.push(document.referrer);
  }
  try {
    if (window.self !== window.top && window.top?.location?.href) {
      urls.push(window.top.location.href);
    }
  } catch {
    // Cross-origin iframe cannot read top.location
  }
  return urls;
}

function hasQueryMarker() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('baiyu')) {
    return true;
  }
  return QUERY_MARKER_KEYS.some(key => {
    const value = params.get(key);
    return value && matchesMarker(value);
  });
}

function resolveEnabled() {
  if (isMarkedEnabled()) {
    baiyuHardwareMenuEnabled.value = true;
    return true;
  }

  if (hasQueryMarker()) {
    markEnabled();
    return true;
  }

  const matched = collectBrowserUrls().some(matchesMarker);
  if (matched) {
    markEnabled();
    return true;
  }

  baiyuHardwareMenuEnabled.value = false;
  return false;
}

/** Call once at app bootstrap (before login redirects). */
export function initBaiyuHardwareEmbedContext() {
  resolveEnabled();
}

/** Parent shell: postMessage({ type: 'SBT_BAIYU_HARDWARE', enabled: true }). */
export function setupBaiyuHardwareEmbedMessageListener() {
  window.addEventListener('message', event => {
    const data = event?.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    if (data.type === 'SBT_BAIYU_HARDWARE' && data.enabled) {
      markEnabled();
    }
  });
}

export default function isBaiyuHardwareEnabled() {
  return resolveEnabled();
}
